import api from "../../utils/api";
import { PLATFORM_IDS } from "./platformCatalog";
import { getCurrentUser } from "../../lib/authSession";

const connectionsStorageKey = (companyId) =>
  companyId ? `repman-client-platform-connections-v1:c-${companyId}` : null;

const googleIntegrationStorageKey = (companyId) =>
  companyId ? `repman-google-integration-id:c-${companyId}` : null;

/**
 * Per-platform plan limits as reported by the server:
 *   { google: {limit, connectedCount, overLimit, atLimit}, jameda: {...}, ... }
 * `limit` null = unlimited (Trial / Enterprise).
 */
export function normalizePlatformLimits(raw) {
  const out = {};
  Object.entries(raw || {}).forEach(([platform, s]) => {
    if (!s || typeof s !== "object") return;
    out[platform] = {
      limit: s.limit == null ? null : Number(s.limit),
      connectedCount: Number(s.connected_count) || 0,
      overLimit: Boolean(s.over_limit),
      atLimit: Boolean(s.at_limit),
    };
  });
  return out;
}

/**
 * Full server snapshot: connection list (also written to the local cache)
 * plus the plan limits. Returns null when the call fails.
 */
export async function hydrateConnectionsSnapshot(
  companyId = getCurrentUser()?.company?.id
) {
  let data;
  try {
    data = await api.get("/api/integrations/connections/", { auth: true });
  } catch {
    return null;
  }
  const list = persistConnectionsFromPayload(data, companyId);
  return {
    list: list || [],
    platformLimits: normalizePlatformLimits(data?.platform_limits),
    limitMessage: data?.limit_message || "",
  };
}

/**
 * Pull the company's connected platforms from the backend and refresh the
 * local cache. The server is the source of truth, so team members (and the
 * same user on another device) see everything the account has connected.
 * Returns the normalized connection list, or null when the call fails.
 */
export async function hydrateConnectionsFromServer(
  companyId = getCurrentUser()?.company?.id
) {
  let data;
  try {
    data = await api.get("/api/integrations/connections/", { auth: true });
  } catch {
    return null;
  }
  return persistConnectionsFromPayload(data, companyId);
}

function persistConnectionsFromPayload(data, companyId) {

  const resolvedCompanyId = data?.company_id ?? companyId;
  const key = connectionsStorageKey(resolvedCompanyId);
  if (!key) return null;

  const list = (data?.connections || [])
    .filter((c) => c && c.location_id)
    .map((c) => {
      const entry = {
        connectionKey: `${c.platform}-${c.location_id}`,
        remoteId: String(c.location_id),
        platformId: c.platform === "jameda" ? "jameda" : "google",
        connectedAt: c.last_synced ? Date.parse(c.last_synced) || Date.now() : Date.now(),
        displayName: c.display_name || "",
        locationId: c.location_id,
      };
      if (c.integration_id) entry.integrationId = c.integration_id;
      const rating = Number(c.average_rating);
      if (Number.isFinite(rating) && rating > 0) {
        entry.avgRating = Math.max(1, Math.min(5, Math.round(rating * 10) / 10));
      }
      return entry;
    });

  try {
    window.localStorage.setItem(key, JSON.stringify(list));
    const iKey = googleIntegrationStorageKey(resolvedCompanyId);
    if (iKey && data?.google_integration_id) {
      window.localStorage.setItem(iKey, String(data.google_integration_id));
    }
  } catch {
    /* ignore quota/private-mode errors */
  }
  return list;
}

function newConnectionKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `conn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeStoredConnections(raw) {
  if (!raw) return [];
  try {
    const data = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    return data
      .filter(
        (c) =>
          c &&
          PLATFORM_IDS.includes(c.platformId) &&
          typeof c.connectedAt === "number" &&
          Number.isFinite(c.connectedAt)
      )
      .map((c) => {
        const o = {
          connectionKey:
            typeof c.connectionKey === "string" && c.connectionKey.length > 0
              ? c.connectionKey
              : newConnectionKey(),
          platformId: c.platformId,
          connectedAt: c.connectedAt,
        };
        if (typeof c.displayName === "string" && c.displayName.trim()) {
          o.displayName = c.displayName.trim();
        }
        if (c.locationId != null) {
          const lid = Number(c.locationId);
          if (Number.isFinite(lid) && lid > 0) o.locationId = lid;
        }
        if (typeof c.remoteId === "string" && c.remoteId.trim()) {
          o.remoteId = c.remoteId.trim();
        }
        return o;
      });
  } catch {
    return [];
  }
}

export function getLocationIdFromConnection(connection) {
  if (!connection) return null;
  if (connection.locationId != null) {
    const lid = Number(connection.locationId);
    if (Number.isFinite(lid) && lid > 0) return lid;
  }
  if (connection.remoteId) {
    const rid = Number(connection.remoteId);
    if (Number.isFinite(rid) && rid > 0) return rid;
  }
  if (
    typeof connection.connectionKey === "string" &&
    connection.connectionKey.startsWith("google-")
  ) {
    const fromKey = Number(connection.connectionKey.slice(7));
    if (Number.isFinite(fromKey) && fromKey > 0) return fromKey;
  }
  return null;
}

/**
 * All review-bearing locations saved from the Platforms page (Google + Jameda).
 * Each entry: { locationId, displayName, platform }.
 */
export function loadReviewLocations(companyId = getCurrentUser()?.company?.id) {
  const key = connectionsStorageKey(companyId);
  if (!key) return [];
  const list = normalizeStoredConnections(
    typeof window !== "undefined" ? window.localStorage.getItem(key) : null
  );
  return list
    .filter((c) => c.platformId === "google" || c.platformId === "jameda")
    .map((c) => {
      const locationId = getLocationIdFromConnection(c);
      return locationId
        ? {
            locationId,
            displayName: c.displayName || `Standort ${locationId}`,
            platform: c.platformId,
          }
        : null;
    })
    .filter(Boolean);
}

/** Google Business locations saved from the Platforms page (for Reviews tab). */
export function loadGoogleLocations(companyId = getCurrentUser()?.company?.id) {
  return loadReviewLocations(companyId).filter((l) => l.platform === "google");
}
